import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import CommonButton from '../Components/CommonButton';
import CommonSearchBar from '../Components/CommonSearchBar';
import CommonTable from '../Components/CommonTable';
import CommonLoader from '../Components/CommonLoader';
import NoDataPage from '../Components/NoDataPage';
import PageHeading from '../Components/PageHeading';
import StatusBadge from '../Components/StatusBadge';
import { useConfirm } from '../Components/ConfirmModal';
import { Body, SubHeading } from '../Constants/Fonts';
import { delete_sso_connection, get_sso_connections, get_sso_domains, update_sso_connection } from '../services';
import {
    CopyValue,
    formatDate,
    readData,
    useCopyValue,
    useSsoRequest,
} from './ssoShared';
import '../styles/ssoAuth.scss';

// The domains a connection signs in, for confirmation copy.
const domainsOf = (connection) => connection?.allowed_domains?.join(', ') || 'its domains';
// What happens to those users when SSO stops for this connection.
const FALLBACK_NOTE = 'will stop signing in through the identity provider and fall back to password login.';

function AllSsoDomains() {
    const navigate = useNavigate();
    const { busy, request, notify, toastNode } = useSsoRequest();
    const { confirm, confirmModal } = useConfirm();
    const { copied, copy } = useCopyValue();

    const [domains, setDomains] = useState([]);
    const [connections, setConnections] = useState([]);
    const [loading, setLoading] = useState(true);
    const [search, setSearch] = useState('');

    const fetchAll = useCallback(async () => {
        try {
            setLoading(true);
            const [domainRes, connectionRes] = await Promise.all([get_sso_domains(), get_sso_connections()]);
            setDomains(readData(domainRes)?.domains || []);
            setConnections(readData(connectionRes) || []);
        } catch (error) {
            console.error('Failed to load SSO data:', error);
            setDomains([]);
            setConnections([]);
            notify('Could not load SSO domains. Please refresh and try again.', 'error');
        } finally {
            setLoading(false);
        }
    }, [notify]);

    useEffect(() => { fetchAll(); }, [fetchAll]);

    // Both actions confirm first (naming the connection + its domains), then fire
    // the call. `request` raises the loading/success/error toasts on its own.
    const handleToggle = async (connection) => {
        const next = !connection?.is_enabled;

        const ok = await confirm({
            title: next ? 'Enable SSO' : 'Disable SSO',
            confirmLabel: next ? 'Yes, Enable' : 'Yes, Disable',
            danger: !next,
            message: next
                ? `Enable SSO for ${connection?.name}? Users on ${domainsOf(connection)} will sign in through the identity provider from now on.`
                : `Disable SSO for ${connection?.name}? Users on ${domainsOf(connection)} ${FALLBACK_NOTE}`,
        });
        if (!ok) return;

        return request(
            'toggle',
            () => update_sso_connection(connection?.id, { is_enabled: next }),
            fetchAll,
            {
                loading: `${next ? 'Enabling' : 'Disabling'} SSO for ${connection?.name}…`,
                success: `SSO ${next ? 'enabled' : 'disabled'} for ${connection?.name}.`,
            }
        );
    };

    const handleDeleteSso = async (connection) => {
        const ok = await confirm({
            title: 'Delete SSO Connection',
            confirmLabel: 'Yes, Delete',
            danger: true,
            message: `Delete the SSO connection ${connection?.name}? Users on ${domainsOf(connection)} ${FALLBACK_NOTE}`,
        });
        if (!ok) return;

        return request(
            'delete',
            () => delete_sso_connection(connection?.id),
            fetchAll,
            {
                loading: `Deleting SSO connection ${connection?.name}…`,
                success: `SSO connection ${connection?.name} deleted.`,
            }
        );
    };

    // One filter box over both tables — matches a domain or a connection name.
    const term = search.trim().toLowerCase();
    const filteredDomains = useMemo(
        () => (term ? domains.filter((item) => item?.domain?.toLowerCase().includes(term)) : domains),
        [domains, term]
    );
    const filteredConnections = useMemo(() => (term
        ? connections.filter((item) => item?.name?.toLowerCase().includes(term)
            || item?.allowed_domains?.some((domain) => domain?.toLowerCase().includes(term)))
        : connections), [connections, term]);

    const connectionRows = filteredConnections.map((item) => ({
        data: [
            <Body as='span' variant={2}>{item?.name || '-'}</Body>,
            <Body as='span' variant={2}>
                {item?.provider_label || item?.provider || '-'}{item?.protocol ? ` · ${item.protocol}` : ''}
            </Body>,
            <Body as='span' variant={2}>
                {item?.allowed_domains?.length ? item.allowed_domains.join(', ') : '-'}
            </Body>,
            item?.idp_cert_expires_at ? (
                <Body as='span' variant={2} color={Number(item?.cert_days_remaining) <= 30 ? 'var(--color-warning)' : undefined}>
                    {formatDate(item.idp_cert_expires_at)}
                    {item?.cert_days_remaining != null && ` (${item.cert_days_remaining} days)`}
                </Body>
            ) : '-',
            <StatusBadge status={item?.is_enabled ? 'enabled' : 'disabled'} />,
            <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                <CommonButton
                    variant={'primary'}
                    text={item?.is_enabled ? 'Disable SSO' : 'Enable SSO'}
                    disable={Boolean(busy.toggle)}
                    onClick={() => handleToggle(item)}
                />
                <CommonButton
                    variant='error'
                    text='Delete'
                    disable={Boolean(busy.delete)}
                    onClick={() => handleDeleteSso(item)}
                />
            </div>,
        ]
    }));

    const domainRows = filteredDomains.map((item) => ({
        data: [
            <Body as='span' variant={2}>{item?.domain || '-'}</Body>,
            <StatusBadge
                variant={item?.is_verified ? 'success' : 'warning'}
                label={item?.is_verified ? 'Verified' : 'Pending'}
            />,
            <Body as='span' variant={2} className='text-capitalize'>
                {item?.verification_method?.replace(/_/g, ' ') || '-'}
            </Body>,
            item?.txt_record?.value
                ? <CopyValue value={item.txt_record.value} copied={copied} onCopy={copy} />
                : '-',
            formatDate(item?.verified_at),
        ]
    }));

    const nothingToShow = !filteredConnections.length && !filteredDomains.length;

    return (
        <div className='sso_auth_wrapper rp1_widget mt-2 cmn_elevated_card'>
            {toastNode}
            {confirmModal}

            <PageHeading
                title='All SSO Domains'
                subtitle='Every configured connection and the domains it signs in. Turning a connection off sends its users back to password login.'
                actions={(
                    <div className='d-flex align-items-center gap-2 cmn_elevated_card'>
                        <div className='sso_search_input'>
                            <CommonSearchBar
                                value={search}
                                onChange={setSearch}
                                placeholder='Search domain or connection'
                            />
                        </div>
                        <CommonButton
                            text='Add New SSO'
                            onClick={() => navigate('/ssoAuth')}
                        />
                    </div>
                )}
            />

            {loading ? (
                <CommonLoader label='Loading SSO domains…' style={{ padding: '48px 0' }} />
            ) : nothingToShow ? (
                <div className='sso_card cmn_elevated_card'>
                    <NoDataPage
                        title={term ? 'No matches' : 'No SSO configured yet'}
                        hint={term
                            ? 'No connection or domain matches your search.'
                            : 'Add a domain and upload its SAML metadata to set up single sign-on.'}
                        action={!term && (
                            <CommonButton text='Add New SSO' onClick={() => navigate('/ssoAuth')} />
                        )}
                    />
                </div>
            ) : (
                <>
                    <div className='sso_card mb-3'>
                        <SubHeading variant={2} color='var(--color-text-primary)' style={{ margin: '0 0 16px' }}>SSO Connections</SubHeading>
                        {filteredConnections.length ? (
                            <div className='sso_table_wrap'>
                                <CommonTable
                                    headers={['Connection', 'Provider', 'Domains', 'Certificate Expiry', 'Status', 'Action']}
                                    rows={connectionRows}
                                />
                            </div>
                        ) : (
                            <NoDataPage title='No connections' hint='No SSO connection matches your search.' minHeight={120} />
                        )}
                    </div>

                    <div className='sso_card'>
                        <SubHeading variant={2} color='var(--color-text-primary)' style={{ margin: '0 0 16px' }}>Domains</SubHeading>
                        {filteredDomains.length ? (
                            <div className='sso_table_wrap'>
                                <CommonTable
                                    headers={['Domain', 'Status', 'Verification Method', 'TXT Record', 'Verified On']}
                                    rows={domainRows}
                                />
                            </div>
                        ) : (
                            <NoDataPage title='No domains' hint='No domain matches your search.' minHeight={120} />
                        )}
                    </div>
                </>
            )}
        </div>
    );
}

export default AllSsoDomains;
